import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { DAILY_TIPS_QUERY } from '@shared/graphql/queries/dailyTips';
import { MainLayout } from '../layouts';
import TipCardV2 from '../components/tips/TipCardV2';
import TipperPerformanceCard from '../components/performance/TipperPerformanceCard';
import { useAnalysisType } from '../contexts/AnalysisTypeContext';
import analyticsService from '../services/analytics';
import '../styles/TipsPage.css';

interface FormData {
  form_data: any;
}

interface Analysis {
  analysis_id: string;
  horse_id: string;
  horse_name: string;
  odds: number;
  assessed_odds: number;
  adjusted_assessed_prob?: number;
  confidence: number;
  analysis_summary: string;
  pros: string;
  cons: string;
  pick_order: number;
  pick_type: string;
  bet_stake?: string;
  bet_type?: string;
  version: number;
  is_latest: boolean;
  created_at: string;
}

interface Result {
  position?: number;
  position_finish?: number;
  horse_name: string;
  barrier?: number;
  horse_number?: number;
  price_sp?: number;
}

interface Runner {
  runner_id: string;
  race_id: string;
  horse_id: string;
  tab_number: number;
  barrier: number;
  horse_name: string;
  jockey: string;
  trainer: string;
  weight: number;
  created_at: string;
  form_data?: FormData;
  result?: Result;
}

interface Race {
  race_id: string;
  meeting_id: string;
  race_number: number;
  race_name: string;
  distance: number;
  race_time: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Track {
  track_id: string;
  name: string;
  code: string;
  state: string;
  type: string;
  created_at: string;
  updated_at: string;
}

interface TrackConditions {
  weather?: string;
  trackCondition?: string;
  trackConditionNumber?: string;
  penetrometer?: number;
  rail?: string;
  lastUpdate?: string;
  abandoned?: boolean;
}

interface PredictedTrackConditions {
  predictedWeather?: string;
  predictedTrackCondition?: string;
  predictedTrackConditionNumber?: number;
  predictedPenetrometer?: number;
  predictionConfidence?: string;
  predictionDate?: string;
}

interface Meeting {
  meeting_id: string;
  track_id: string;
  meeting_date: string;
  race_count: number;
  status: string;
  is_private: boolean;
  is_training: boolean;
  meeting_note?: string;
  meeting_note_type?: string;
  created_at: string;
  updated_at: string;
  track: Track;
  trackConditions?: TrackConditions;
  predictedTrackConditions?: PredictedTrackConditions;
}

interface DailyTip {
  id: number;
  tip_date: string;
  rank: number;
  description?: string;
  units?: number;
  bet_type?: string;
  meeting: Meeting;
  race: Race;
  runner: Runner;
  analysis: Analysis;
  created_at: string;
  updated_at: string;
}

interface DailyTipsContent {
  id: number;
  meeting_date: string;
  heading: string;
  content: string;
  created_at: string;
  updated_at: string;
}

interface DailyTipsScratching {
  raceNo: number;
  tabNo?: number;
  runnerId?: string;
  runnerName: string;
  reason?: string;
  scratched_at?: string;
}

interface DailyTipsData {
  dailyTipsDaily: {
    date: string;
    total_tips: number;
    tips: DailyTip[];
    meetings: Meeting[];
    content?: DailyTipsContent;
    scratchings?: DailyTipsScratching[];
  } | null;
}

interface ExpandedState {
  [key: string]: boolean;
}

// Helper function to extract units from bet_stake string (for backwards compatibility)
const getUnitsFromBetStake = (betStake: string): number => {
  const match = betStake.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
};

/* ─────────────────────────────────────────────────────────
   TIPPA'S MAIL — HOLDING SCREEN (V2 in progress)
   To restore: remove the early return below and re-enable
   the full component body that follows.
   ───────────────────────────────────────────────────────── */
const TippaMailHolding: React.FC = () => (
  <MainLayout>
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16">
      <div className="max-w-lg w-full text-center">

        {/* Icon */}
        <div className="w-16 h-16 rounded-2xl bg-green-50 border border-green-100 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-racing-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-heading font-bold text-gray-900 mb-3">
          Tippa's Mail is taking a break.
        </h1>

        {/* Body */}
        <div className="text-gray-500 text-base leading-relaxed space-y-3 mb-8">
          <p>
            We're rebuilding it from the ground up so it actually delivers what it promises.
            The V2 is going to be worth the wait — but for now, it's offline.
          </p>
          <p>We'll let you know as soon as it's back.</p>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 pt-7">
          <p className="text-sm text-gray-400 mb-4">In the meantime, check out Tippa Picks —</p>
          <p className="text-sm text-gray-500 mb-5">
            We've crunched every race and pulled out the runners our model rates above 55% assessed probability — that's each runner's calculated chance of winning based on form, speed maps, and market data.
            Your best shortlist for the day, ready to go.
          </p>
          <Link
            to="/high-probability"
            className="inline-flex items-center gap-2 bg-racing-green text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-racing-green-dark transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm0-13a5 5 0 1 0 5 5 5.006 5.006 0 0 0-5-5Zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3Zm0-4a1 1 0 1 0 1 1 1 1 0 0 0-1-1Z" />
            </svg>
            View Tippa Picks
          </Link>
        </div>

      </div>
    </div>
  </MainLayout>
);

const DailyTipsPage: React.FC = () => {
  return <TippaMailHolding />;
};


// Full DailyTipsPage V1 body removed — restore from git if needed.

export default DailyTipsPage;
